import 'src/styles/ui/empty-state.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

const renderIcon = icon => {
  if (icon == null) return null;
  return React.isValidElement(icon) ? icon : <FontAwesomeIcon icon={icon} />;
};

/**
 * Placeholder shown when a list or section has nothing to display. Three shapes:
 *  - 'panel' (default): a soft dashed card — icon tile, title, message, optional action.
 *  - 'note': a centered muted sentence (inline "nothing here yet" copy).
 *  - 'inline': a bordered row with an icon and a short message.
 * Generic: usable by any feature. `icon` accepts a FontAwesome definition or a node.
 */
const EmptyState = ({ variant, icon, title, children, action, className, ...props }) => {
  if (variant === 'note') {
    return (
      <div className={classNames('empty-state empty-state--note', className)} {...props}>
        {children}
      </div>
    );
  }
  if (variant === 'inline') {
    return (
      <div className={classNames('empty-state empty-state--inline', className)} {...props}>
        {renderIcon(icon)}
        {children}
      </div>
    );
  }
  return (
    <div className={classNames('empty-state empty-state--panel', className)} {...props}>
      {icon != null && <span className='empty-state__icon'>{renderIcon(icon)}</span>}
      <div>
        {title != null && <div className='empty-state__title'>{title}</div>}
        {children != null && <div className='empty-state__text'>{children}</div>}
        {action}
      </div>
    </div>
  );
};

EmptyState.propTypes = {
  variant: PropTypes.oneOf(['panel', 'note', 'inline']),
  icon: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
  title: PropTypes.node,
  children: PropTypes.node,
  action: PropTypes.node,
  className: PropTypes.string,
};

EmptyState.defaultProps = {
  variant: 'panel',
  icon: null,
  title: null,
  children: null,
  action: null,
  className: '',
};

export default EmptyState;
